import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './Profile.css';

import Header from '../../shared/layout/Header';

import MapContainerComponent from './components/map/MapContainer';
import { POIMapClickHandler, POIMarkers } from './components/poi/POIPanel';
import { fetchNearbyPOIs } from './services/poiService';
import api from './services/api';
import developersApi from '../../api/services/developerService';

import PropertyPanel from './components/property/PropertyPanel';
import POIPanel from './components/poi/POIPanel';
import DreamHomePanel from './components/preferences/DreamHomePanel';
import AgentManagement from './components/agents/AgentManagement';
import UserInfoPanel from './components/user/UserInfoPanel';
import FavoritesPanel from './components/user/FavoritesPanel';
import ReservationsPanel from './components/user/ReservationsPanel';
import ShowingsPanel from './components/user/ShowingsPanel';
import RentedPropertiesPanel from './components/user/RentedPropertiesPanel';
import PropertyRequestsPanel from './components/user/PropertyRequestsPanel';
import DeveloperProfilePanel from './components/developer/DeveloperProfilePanel';
import MessagesPanel from '../MainPage/MessagesPanel';

const POI_TYPES = 'bus_station,school,hospital,pharmacy,restaurant,supermarket';

function Profile() {

  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('properties');
  const userProfile = JSON.parse(localStorage.getItem('user_profile') || '{}');
  const [isDeveloper, setIsDeveloper] = useState(
    Boolean(userProfile.is_developer || userProfile.developer_id)
  );

  useEffect(() => {
    if (isDeveloper) return;
    developersApi.listFirms().then((firms) => {
      const firm = firms[0];
      if (!firm) return;
      setIsDeveloper(true);
      localStorage.setItem('user_profile', JSON.stringify({
        ...userProfile,
        is_developer: true,
        developer_id: firm.id,
        developer_name: firm.display_name || firm.legal_name || '',
      }));
    }).catch(() => {
      // Non-developer users legitimately receive an empty result or may lack access.
    });
  // Membership only needs to be discovered once when the profile page opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // POI state lifted here so map and panel share it
  const [poiList, setPoiList] = useState([]);
  const [poiSelectedLocation, setPoiSelectedLocation] = useState(null);
  const [poiPickingLocation, setPoiPickingLocation] = useState(false);

  const handlePoiMapClick = async (latlng) => {
    if (!poiPickingLocation) return;
    setPoiSelectedLocation([latlng.lat, latlng.lng]);
    setPoiPickingLocation(false);
    try {
      const data = await fetchNearbyPOIs(latlng.lat, latlng.lng, 1000, POI_TYPES);
      setPoiList((data.features || []).map(f => ({
        ...f,
        properties: { ...f.properties, osm_id: f.properties.osm_id ?? f.id },
      })));
    } catch (err) {

    }
  };

  const sectionsWithMap = ['poi', 'dream-home'];
  const showMap = sectionsWithMap.includes(activeSection);

  const renderActiveSection = () => {

    switch (activeSection) {
            case 'user-info':
        return <UserInfoPanel />;

      case 'properties':
        return <PropertyPanel />;

      case 'poi':
        return (
          <POIPanel
            pois={poiList}
            setPois={setPoiList}
            selectedLocation={poiSelectedLocation}
            setSelectedLocation={setPoiSelectedLocation}
            pickingLocation={poiPickingLocation}
            setPickingLocation={setPoiPickingLocation}
          />
        );

      case 'dream-home':
        return <DreamHomePanel />;

      case 'agents':
        return <AgentManagement />;

      case 'favorites':
        return <FavoritesPanel />;

      case 'reservations':
        return <ReservationsPanel />;

      case 'showings':
        return <ShowingsPanel />;

      case 'rented':
        return (
          <RentedPropertiesPanel />
        );

      case 'property-requests':
        return <PropertyRequestsPanel />;

      case 'developer-profile':
        return <DeveloperProfilePanel />;

      case 'messages':
        return (
          <div className="mp-panel-embedded">
            <MessagesPanel onClose={() => setActiveSection('user-info')} />
          </div>
        );

      default:
        return <UserInfoPanel />;
    }
  };

  return (

    <div className="profile-page">

      <Header />

      <div className="profile-layout">

        {/* SIDEBAR */}

        <aside className="profile-sidebar">

          <div className="sidebar-title">
            پنل کاربری
          </div>
          <button
            className={`sidebar-btn ${
              activeSection ===
              'user-info'
                ? 'active'
                : ''
            }`}
            onClick={() =>
              setActiveSection(
                'user-info'
              )
            }
          >
            اطلاعات کاربری
          </button>
          {isDeveloper && (
            <button
              className={`sidebar-btn ${activeSection === 'developer-profile' ? 'active' : ''}`}
              onClick={() => setActiveSection('developer-profile')}
            >
              پروفایل و تایید سازنده
            </button>
          )}
          <button
            className={`sidebar-btn ${
              activeSection === 'properties'
                ? 'active'
                : ''
            }`}
            onClick={() =>
              setActiveSection(
                'properties'
              )
            }
          >
            املاک من
          </button>

          <button
            className={`sidebar-btn ${
              activeSection === 'poi'
                ? 'active'
                : ''
            }`}
            onClick={() =>
              setActiveSection('poi')
            }
          >
            امکانات اطراف
          </button>

          <button
            className={`sidebar-btn ${
              activeSection ===
              'dream-home'
                ? 'active'
                : ''
            }`}
            onClick={() =>
              setActiveSection(
                'dream-home'
              )
            }
          >
            خانه رویایی
          </button>

          <button
            className={`sidebar-btn ${
              activeSection ===
              'agents'
                ? 'active'
                : ''
            }`}
            onClick={() =>
              setActiveSection(
                'agents'
              )
            }
          >
            نمایندگان
          </button>

          <button
            className={`sidebar-btn ${
              activeSection ===
              'favorites'
                ? 'active'
                : ''
            }`}
            onClick={() =>
              setActiveSection(
                'favorites'
              )
            }
          >
            علاقه‌مندی‌ها
          </button>

          <button
            className={`sidebar-btn ${
              activeSection ===
              'reservations'
                ? 'active'
                : ''
            }`}
            onClick={() =>
              setActiveSection(
                'reservations'
              )
            }
          >
            رزروها
          </button>

          <button
            className="sidebar-btn"
            onClick={() => navigate('/transactions')}
          >
            معاملات من
          </button>

          <button
            className={`sidebar-btn ${
              activeSection ===
              'showings'
                ? 'active'
                : ''
            }`}
            onClick={() =>
              setActiveSection(
                'showings'
              )
            }
          >
            بازدیدها
          </button>

          <button
            className={`sidebar-btn ${
              activeSection ===
              'rented'
                ? 'active'
                : ''
            }`}
            onClick={() =>
              setActiveSection(
                'rented'
              )
            }
          >
            املاک اجاره شده
          </button>

          <button
            className={`sidebar-btn ${
              activeSection === 'messages'
                ? 'active'
                : ''
            }`}
            onClick={() => setActiveSection('messages')}
          >
            💬 پیام‌ها
          </button>

          <button
            className={`sidebar-btn ${
              activeSection === 'property-requests'
                ? 'active'
                : ''
            }`}
            onClick={() => setActiveSection('property-requests')}
          >
            🏡 درخواست‌های یافتن خانه
          </button>

        </aside>

        {/* CONTENT */}

        <main className="profile-content">

          <div className="panel-wrapper">
            {renderActiveSection()}
          </div>

          {showMap && (
            <div className="map-wrapper">
              <MapContainerComponent
                activeSection={activeSection}
                poiMapElements={
                  activeSection === 'poi' ? (
                    <>
                      {poiPickingLocation && <POIMapClickHandler onMapClick={handlePoiMapClick} />}
                      <POIMarkers
                        pois={poiList}
                        onVerify={async (osmId) => {
                          await api.post(`/pois/${osmId}/verify/`, {});
                          if (poiSelectedLocation) {
                            const data = await fetchNearbyPOIs(poiSelectedLocation[0], poiSelectedLocation[1], 1000, POI_TYPES);
                            setPoiList((data.features || []).map(f => ({ ...f, properties: { ...f.properties, osm_id: f.properties.osm_id ?? f.id } })));
                          }
                        }}
                        onEdit={() => {}}
                        onDelete={async (osmId) => {
                          await api.delete(`/pois/${osmId}/remove/`);
                          setPoiList(prev => prev.filter(p => p.properties.osm_id !== osmId));
                        }}
                      />
                    </>
                  ) : null
                }
              />
            </div>
          )}

        </main>

      </div>

    </div>
  );
}

export default Profile;
